// Seeded 2D noise for the terrain: value noise on an integer lattice whose corners come from the seed's hash (no
// tables, no state), smoothed with the quintic fade, summed in octaves. Pure: the same seed and point give the same
// value whatever was asked before.
import { subSeed } from "./seeds";

/** The lattice corner's value in [-1, 1]. */
function corner(seed: number, i: number, j: number): number {
  return (subSeed(seed, "", i, j) / 4294967295) * 2 - 1;
}

const fade = (t: number): number => t * t * t * (t * (t * 6 - 15) + 10);

/** Value noise at (x, z) in lattice units: in [-1, 1]. */
export function valueNoise(seed: number, x: number, z: number): number {
  const i = Math.floor(x);
  const j = Math.floor(z);
  const u = fade(x - i);
  const v = fade(z - j);
  const a = corner(seed, i, j);
  const b = corner(seed, i + 1, j);
  const c = corner(seed, i, j + 1);
  const d = corner(seed, i + 1, j + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}

/** Octaves of value noise: each half the wavelength and amplitude of the last, normalised to [-1, 1]. `ridged` folds
 *  each octave (1 − |n|) into ridges. */
export function fbm(seed: number, x: number, z: number, scale: number, octaves = 1, ridged = false): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let f = 1 / scale;
  for (let o = 0; o < octaves; o++) {
    const n = valueNoise(seed + o * 0x9e3779b9, x * f, z * f);
    sum += (ridged ? 1 - 2 * Math.abs(n) : n) * amp;
    norm += amp;
    amp *= 0.5;
    f *= 2;
  }
  return sum / norm;
}
